import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../api/client';
import { ForecastRegion, ForecastLocation } from '../api/types';
import { useI18n } from '../i18n/I18nContext';

interface MapContextType {
  regions: ForecastRegion[];
  mapInstance: any | null;
  isLoading: boolean;
  error: string | null;
  setMapInstance: (instance: any) => void;
  findLocationById: (id: string) => ForecastLocation | null;
  retryLoad: () => void;
  /* La cella del windgram di un punto (pressione lunga): resta disegnata sulla mappa anche
   * chiuso il windgram, cosi' si vede a quale cella si riferiva. */
  selectedCell: SelectedCell | null;
  setSelectedCell: (c: SelectedCell | null) => void;
}

export interface SelectedCell {
  point: [number, number];          // dove si e' premuto, [lat, lon]
  cell: [number, number][] | null;  // angoli della cella, quando la risposta e' arrivata
}

const MapContext = createContext<MapContextType | undefined>(undefined);

export const useMapContext = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapContext must be used within a MapProvider');
  }
  return context;
};

interface MapProviderProps {
  children: ReactNode;
  onLogout: () => void;
}

export const MapProvider: React.FC<MapProviderProps> = ({ children, onLogout }) => {
  const { t } = useI18n();
  const [regions, setRegions] = useState<ForecastRegion[]>([]);
  const [mapInstance, setMapInstance] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

  const findLocationById = useCallback((id: string): ForecastLocation | null => {
    for (const region of regions) {
      const location = region.windgram_list.find(loc => loc.windgram_id === id);
      if (location) {
        return location;
      }
    }
    return null;
  }, [regions]);

  const loadForecastLocations = useCallback(async () => {
    setIsLoading(true);
    const loadStartTime = Date.now();
    
    try {
      const result = await apiClient.getForecastLocations((updatedForecastLocations) => {
        setRegions(updatedForecastLocations);
      });

      if (result.success && result.data) {
        setRegions(result.data);
        setError(null); // Only clear error on success
      } else {
        setError(result.error || t('map.loadError'));
      }
    } catch (err) {
      setError(t('map.networkError'));
    } finally {
      // Ensure loading toast is visible for at least 300ms
      const loadDuration = Date.now() - loadStartTime;
      const minDisplayTime = 300;
      if (loadDuration < minDisplayTime) {
        setTimeout(() => setIsLoading(false), minDisplayTime - loadDuration);
      } else {
        setIsLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    loadForecastLocations();
  }, [loadForecastLocations]);

  const value: MapContextType = {
    regions,
    mapInstance,
    isLoading,
    error,
    setMapInstance,
    findLocationById,
    retryLoad: loadForecastLocations,
    selectedCell,
    setSelectedCell
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};
